import { normalizeEntityType } from "../components/relationships/SectionConfig";
import { userCanContribute } from "./userRoles";

export function isExplorerCatalogEntity(entity) {
  return Boolean(entity);
}

export function filterExplorerBrowseEntities(entities) {
  return Array.isArray(entities) ? entities : [];
}

/** Contributors may see their own proposed entities (API scopes by owner). */
export function userMaySeeProposedCatalog() {
  if (!userCanContribute()) return false;
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u) return false;
    return Boolean(String(u.id || u.user_id || "").trim());
  } catch {
    return false;
  }
}

/** Editors/admins may merge and steward org-wide catalog operations. */
export function userIsCatalogSteward() {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "null");
    if (!u) return false;
    if (u.is_super_user) return true;
    const r = String(u.role || "").trim();
    return r === "Administrator" || r === "Editor";
  } catch {
    return false;
  }
}

function normId(id) {
  return String(id || "").trim().toLowerCase();
}

function isProvidesConsumes(relType) {
  const t = String(relType || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return t === "provides" || t === "consumes";
}

function entityTypeFromMap(
  entityId,
  relatedEntities,
  entityIndex,
  centerEntityId = "",
  centerEntityType = ""
) {
  const key = normId(entityId);
  if (centerEntityId && key === normId(centerEntityId)) {
    const ct = normalizeEntityType(centerEntityType);
    if (ct) return ct;
  }
  if (entityIndex?.get) {
    const fromIndex = entityIndex.get(key);
    if (fromIndex) {
      return normalizeEntityType(fromIndex.type || fromIndex.entity_type);
    }
  }
  const fromRelated = relatedEntities?.[entityId] || relatedEntities?.[key];
  if (fromRelated) {
    return normalizeEntityType(fromRelated.type || fromRelated.entity_type);
  }
  return "";
}

/** Interface visible on an application explorer view (app provides/consumes). */
export function interfaceLinkedToApplication(
  interfaceId,
  relationships,
  relatedEntities,
  applicationId,
  entityIndex = null
) {
  const iid = normId(interfaceId);
  const aid = normId(applicationId);
  if (!iid || !aid) return false;

  for (const rel of relationships || []) {
    if (!isProvidesConsumes(rel.type)) continue;
    const sid = normId(rel.source_entity_id);
    const tid = normId(rel.target_entity_id);
    if (sid !== iid && tid !== iid) continue;

    const otherId = sid === iid ? tid : sid;
    if (otherId === aid) return true;
  }
  return false;
}

export function filterApplicationExplorerPayload(entity, relationships, relatedEntities) {
  const centerType = normalizeEntityType(entity?.type || entity?.entity_type);
  if (centerType !== "application") {
    return {
      relationships: relationships || [],
      relatedEntities: relatedEntities || {},
    };
  }

  const appId = entity?.id;
  const appType =
    normalizeEntityType(entity?.type || entity?.entity_type) || "application";
  const rels = Array.isArray(relationships) ? [...relationships] : [];
  const related = { ...(relatedEntities || {}) };

  const linkedInterfaceIds = new Set();
  const typeAt = (id) =>
    entityTypeFromMap(id, related, null, appId, appType);

  rels.forEach((rel) => {
    if (!isProvidesConsumes(rel.type)) return;
    const sid = rel.source_entity_id;
    const tid = rel.target_entity_id;
    const st = typeAt(sid);
    const tt = typeAt(tid);
    if (tt === "interface" && st === "application") linkedInterfaceIds.add(String(tid));
    if (st === "interface" && tt === "application") linkedInterfaceIds.add(String(sid));
    const sidNorm = normId(sid);
    const tidNorm = normId(tid);
    const appNorm = normId(appId);
    if (sidNorm === appNorm) {
      const ot = typeAt(tid);
      if (ot === "interface" || ot === "") linkedInterfaceIds.add(String(tid));
    } else if (tidNorm === appNorm) {
      const ot = typeAt(sid);
      if (ot === "interface" || ot === "") linkedInterfaceIds.add(String(sid));
    }
  });

  const filteredRelated = {};
  Object.entries(related).forEach(([id, info]) => {
    const t = normalizeEntityType(info?.type || info?.entity_type);
    if (t === "interface" && !linkedInterfaceIds.has(id)) return;
    filteredRelated[id] = info;
  });

  return { relationships: rels, relatedEntities: filteredRelated };
}

export function filterExplorerInterfaceSectionItems(
  items,
  currentEntity,
  relationships,
  relatedEntities,
  entityIndex
) {
  const centerType = normalizeEntityType(
    currentEntity?.type || currentEntity?.entity_type
  );
  if (centerType !== "application") return items || [];

  return (items || []).filter((item) => {
    const itemType = normalizeEntityType(item?.type || item?.entity_type);
    if (itemType !== "interface") return true;
    return interfaceLinkedToApplication(
      item.id,
      relationships,
      relatedEntities,
      currentEntity.id,
      entityIndex
    );
  });
}
